import type { CurrencyParts } from "@/lib/formatting/types/currency.types";

/**
 * Egypt's locale tag. `Intl` resolves a BARE `ar` to the `latn` numbering
 * system, so `formatCurrency(2045, "ar")` rendered "2,045" — Latin digits
 * sitting inside an interface whose own chrome is Arabic-Indic (the range pills
 * read ٢٤س / ٣٠ي). `ar-EG` resolves to `arab` and gives "٢٬٠٤٥".
 *
 * KNOWN GAP: this converts money but NOT the counts beside it. Plural strings
 * interpolate through `String(count)` in `lib/i18n/intl.tsx`, which bypasses
 * locale formatting entirely — so "٩٩٠ ج.م" still sits directly above
 * "5 طلبات". Closing it means touching every plural string in the portal, so it
 * is deliberately out of scope here rather than silently assumed fixed.
 */
const ARABIC_LOCALE = "ar-EG";

/**
 * CLDR's Arabic EGP symbol is "ج.م." — with a trailing full stop. Mid-figure it
 * reads as the end of a sentence, and it collides with the "·" separators the
 * dashboard uses ("‏٩٩٠ ج.م.‏ · ٥ طلبات"). House style drops it.
 */
const CLDR_EGP_AR = "ج.م.";
const HOUSE_EGP_AR = "ج.م";

/**
 * The whitespace `Intl` puts between figure and symbol. Once the two are
 * separate elements the gap is margin, so this literal is dropped — but ONLY
 * the whitespace: the zero-width bidi marks (U+200F/U+200E) `Intl` emits as
 * literals too must survive, or the figure can reorder in a mixed run.
 */
const SEPARATOR_WHITESPACE = /\s+/g;

/**
 * Money for the seller portal.
 *
 * The returned string keeps `Intl`'s RLM marks (U+200F) around the figure: they
 * are what stop the amount and its symbol from reordering when the value sits
 * in a mixed Arabic/Latin run. Do not strip them.
 */
export function formatCurrency(
  amount: number,
  locale: string,
  currency = "EGP",
): string {
  const resolved = locale === "ar" ? ARABIC_LOCALE : locale;
  const formatted = new Intl.NumberFormat(resolved, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);

  return formatted.replace(CLDR_EGP_AR, HOUSE_EGP_AR);
}

/**
 * The same money, split so the unit can be styled as a unit.
 *
 * Rendering "٩٩٠ ج.م" as one run at one size makes the symbol read as part of
 * the figure. The approved design subordinates it everywhere a number is the
 * headline — number at full size/weight, unit at roughly half, muted, with a
 * small logical gap. That is a typographic decision, so it needs the two pieces
 * separately rather than one pre-joined string.
 *
 * `amount` keeps the RLM marks that `Intl` puts around the figure — they are
 * what stop it reordering inside a mixed Arabic/Latin run.
 *
 * `unitFirst` reports CLDR's order for the resolved locale, because it differs:
 * Arabic trails the symbol ("٢٬٠٤٥ ج.م"), English leads with it ("EGP 2,045").
 * Rendering both in the Arabic order would produce "2,045EGP".
 */
export function formatCurrencyParts(
  amount: number,
  locale: string,
  currency = "EGP",
): CurrencyParts {
  const resolved = locale === "ar" ? ARABIC_LOCALE : locale;
  const parts = new Intl.NumberFormat(resolved, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).formatToParts(amount);

  const unit = parts
    .filter((part) => part.type === "currency")
    .map((part) => part.value.replace(CLDR_EGP_AR, HOUSE_EGP_AR))
    .join("");

  // Everything that is not the symbol, minus the whitespace that used to sit
  // between the two — the gap is now margin, not a space character. Literals
  // are kept otherwise, which is what preserves the bidi marks.
  const value = parts
    .filter((part) => part.type !== "currency")
    .map((part) =>
      part.type === "literal"
        ? part.value.replace(SEPARATOR_WHITESPACE, "")
        : part.value,
    )
    .join("");

  const unitIndex = parts.findIndex((part) => part.type === "currency");
  const figureIndex = parts.findIndex(
    (part) => part.type !== "currency" && part.type !== "literal",
  );

  return {
    amount: value,
    unit,
    unitFirst: unitIndex >= 0 && figureIndex >= 0 && unitIndex < figureIndex,
  };
}
